import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { ArrowRight, Check, Shield, Calendar, FileText } from 'lucide-react';

export default function ServicesPage() {
  const { t } = useLanguage();
  const navigate = useNavigate();

  const handleCTA = () => {
    navigate('/application');
  };

  return (
    <div className="min-h-screen bg-white" data-testid="services-page">
      {/* Header */}
      <section className="py-20 lg:py-28 px-6 lg:px-12 bg-[#F5F3EF]" data-testid="services-hero">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-xs tracking-[0.25em] uppercase text-[#0B3D2E] font-semibold mb-4">
            {t('svc.label')}
          </p>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-[#2B2B2B] tracking-tight font-light leading-tight">
            {t('svc.title')}
          </h1>
          <p className="mt-5 text-base text-[#5a5a5a] leading-relaxed max-w-2xl mx-auto">
            {t('svc.subtitle')}
          </p>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-20 lg:py-28 px-6 lg:px-12" data-testid="pricing-section">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12">
            {/* Engagement Fee */}
            <div className="border border-[#2B2B2B]/10 rounded-lg p-8 lg:p-10 hover-lift" data-testid="engagement-fee-card">
              <div className="w-10 h-10 rounded-md bg-[#0B3D2E]/5 flex items-center justify-center mb-6">
                <FileText className="w-5 h-5 text-[#0B3D2E]" />
              </div>
              <p className="text-xs tracking-[0.2em] uppercase text-[#5a5a5a] font-semibold mb-2">
                {t('svc.engagement')}
              </p>
              <div className="flex items-baseline gap-1 mb-4">
                <span className="font-heading text-4xl lg:text-5xl text-[#2B2B2B] tracking-tight">CHF 1,500</span>
              </div>
              <p className="text-sm text-[#5a5a5a] leading-relaxed mb-6">
                {t('svc.engagementDesc')}
              </p>
              <ul className="space-y-3">
                {['svc.e1', 'svc.e2', 'svc.e3', 'svc.e4'].map(k => (
                  <li key={k} className="flex items-start gap-2.5">
                    <Check className="w-4 h-4 text-[#0B3D2E] mt-0.5 shrink-0" />
                    <span className="text-sm text-[#2B2B2B]">{t(k)}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Success Fee */}
            <div className="border-2 border-[#0B3D2E] rounded-lg p-8 lg:p-10 relative hover-lift" data-testid="success-fee-card">
              <div className="absolute -top-3 left-8">
                <span className="bg-[#0B3D2E] text-white text-[10px] tracking-[0.15em] uppercase font-semibold px-3 py-1 rounded-sm">
                  {t('svc.onSuccess')}
                </span>
              </div>
              <div className="w-10 h-10 rounded-md bg-[#0B3D2E]/5 flex items-center justify-center mb-6">
                <Shield className="w-5 h-5 text-[#0B3D2E]" />
              </div>
              <p className="text-xs tracking-[0.2em] uppercase text-[#5a5a5a] font-semibold mb-2">
                {t('svc.success')}
              </p>
              <div className="flex items-baseline gap-1 mb-4">
                <span className="font-heading text-4xl lg:text-5xl text-[#2B2B2B] tracking-tight">{t('svc.firstRent')}</span>
              </div>
              <p className="text-sm text-[#5a5a5a] leading-relaxed mb-6">
                {t('svc.successDesc')}
              </p>
              <ul className="space-y-3">
                {['svc.s1', 'svc.s2', 'svc.s3'].map(k => (
                  <li key={k} className="flex items-start gap-2.5">
                    <Check className="w-4 h-4 text-[#0B3D2E] mt-0.5 shrink-0" />
                    <span className="text-sm text-[#2B2B2B]">{t(k)}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Commitment Terms */}
      <section className="py-16 lg:py-24 px-6 lg:px-12 bg-[#F5F3EF]" data-testid="terms-section">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-start gap-5 mb-10">
            <div className="w-10 h-10 rounded-md bg-[#0B3D2E]/5 flex items-center justify-center shrink-0 mt-1">
              <Calendar className="w-5 h-5 text-[#0B3D2E]" />
            </div>
            <div>
              <h3 className="font-heading text-xl text-[#2B2B2B] mb-2">{t('svc.commitTitle')}</h3>
              <p className="text-sm text-[#5a5a5a] leading-relaxed">{t('svc.commitDesc')}</p>
            </div>
          </div>
          <div className="bg-white rounded-lg border border-[#2B2B2B]/10 p-6 lg:p-8" data-testid="terms-summary">
            <h4 className="text-xs tracking-[0.2em] uppercase text-[#5a5a5a] font-semibold mb-4">{t('svc.summaryTitle')}</h4>
            <div className="space-y-3">
              {['svc.t1', 'svc.t2', 'svc.t3', 'svc.t4'].map(k => (
                <div key={k} className="flex items-start gap-2.5">
                  <div className="w-1.5 h-1.5 rounded-full bg-[#0B3D2E] mt-2 shrink-0" />
                  <span className="text-sm text-[#2B2B2B] leading-relaxed">{t(k)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 lg:py-24 px-6 lg:px-12" data-testid="services-cta">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="font-heading text-2xl sm:text-3xl text-[#2B2B2B] tracking-tight mb-4">{t('svc.ctaTitle')}</h2>
          <p className="text-sm text-[#5a5a5a] mb-8">{t('svc.ctaDesc')}</p>
          <Button onClick={handleCTA} className="bg-[#0B3D2E] hover:bg-[#0f523e] text-white px-10 py-3 h-auto text-base font-medium" data-testid="services-cta-btn">
            {t('hero.cta')} <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 bg-[#2B2B2B] text-white/50 text-center text-xs" data-testid="footer">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center justify-center gap-2 mb-3">
            <button type="button" onClick={() => navigate('/')} className="flex items-center gap-2 text-white/70 hover:text-white transition-colors" data-testid="services-footer-home-btn">
              <img src="https://static.prod-images.emergentagent.com/jobs/28fc0be7-4196-4b45-b5b2-7034b2e56bae/images/d88baf08cf9aab8cd54854b5018978ec360b4e238c0fbb7dabe14674d502f36a.png" alt="Nest Geneva" className="h-7 w-auto invert" />
              <span className="font-heading text-sm">Nest Geneva</span>
            </button>
          </div>
          <p>&copy; {new Date().getFullYear()} Nest Geneva. All rights reserved.</p>
          <div className="mt-3">
            <button
              type="button"
              onClick={() => navigate('/admin')}
              className="text-[10px] text-white/35 hover:text-white/55 transition-colors tracking-wide"
              data-testid="footer-admin-link"
            >
              Admin access
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
}
